import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import type { Team, Player, PlayerClassification } from '../types';
import { useProfile } from './ProfileContext';
import { trackEvent } from '../lib/analytics';
import {
  getAllTeams,
  createTeam as dbCreateTeam,
  createPlayer as dbCreatePlayer,
  getPlayersByTeam,
  deletePlayer as dbDeletePlayer,
  archivePlayer as dbArchivePlayer,
  mergePlayers as dbMergePlayers,
  getCurrentTeamMember,
  updateTeamName as dbUpdateTeamName,
  deleteTeam as dbDeleteTeam,
} from '../db/supabase-operations';

interface TeamMembership {
  id: string;
  role: string;
  isAdmin: boolean;
  isHeadCoach: boolean;
}

interface TeamContextType {
  teams: Team[];
  teamsLoading: boolean;
  currentTeam: Team | null;
  players: Player[];
  currentMembership: TeamMembership | null;
  setCurrentTeam: (team: Team) => void;
  createTeam: (name: string) => Promise<Team>;
  createPlayer: (name: string, dateOfBirth: string, classification?: PlayerClassification) => Promise<Player>;
  deletePlayer: (id: string) => Promise<void>;
  archivePlayer: (id: string, archived?: boolean) => Promise<void>;
  mergePlayers: (keepPlayerId: string, mergePlayerId: string) => Promise<void>;
  renameTeam: (newName: string) => Promise<void>;
  removeTeam: () => Promise<void>;
  loadPlayers: () => Promise<void>;
  loadTeams: () => Promise<void>;
}

const TeamContext = createContext<TeamContextType | undefined>(undefined);

export const TeamProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { activeProfile, loading: profileLoading } = useProfile();
  const [teams, setTeams] = useState<Team[]>([]);
  const [teamsLoading, setTeamsLoading] = useState(true);
  const [currentTeam, setCurrentTeam] = useState<Team | null>(null);
  const [players, setPlayers] = useState<Player[]>([]);
  const [currentMembership, setCurrentMembership] = useState<TeamMembership | null>(null);

  const loadTeams = async () => {
    setTeamsLoading(true);
    try {
      const loadedTeams = await getAllTeams();
      setTeams(loadedTeams);
      const savedTeamId = activeProfile
        ? localStorage.getItem(`activeTeam_${activeProfile.id}`)
        : null;
      setCurrentTeam(current => {
        const savedTeam = savedTeamId
          ? loadedTeams.find(team => team.id === savedTeamId)
          : null;
        const retainedTeam = current
          ? loadedTeams.find(team => team.id === current.id)
          : null;
        return savedTeam || retainedTeam || loadedTeams[0] || null;
      });
    } catch {
      setTeams([]);
      setCurrentTeam(null);
    } finally {
      setTeamsLoading(false);
    }
  };

  const handleSetCurrentTeam = (team: Team) => {
    setCurrentTeam(team);
    if (activeProfile) {
      localStorage.setItem(`activeTeam_${activeProfile.id}`, team.id);
    }
  };

  const loadPlayers = async () => {
    if (!currentTeam) {
      setPlayers([]);
      return;
    }
    try {
      const loadedPlayers = await getPlayersByTeam(currentTeam.id);
      setPlayers(loadedPlayers);
    } catch {
      // ignore
    }
  };

  useEffect(() => {
    if (profileLoading) {
      setTeamsLoading(true);
    } else if (activeProfile) {
      setTeams([]);
      setCurrentTeam(null);
      loadTeams();
    } else {
      setTeams([]);
      setCurrentTeam(null);
      setTeamsLoading(false);
    }
  }, [activeProfile, profileLoading]);

  useEffect(() => {
    if (currentTeam) {
      loadPlayers();
    } else {
      setPlayers([]);
    }
  }, [currentTeam]);

  // Load current user's membership for the selected team
  useEffect(() => {
    const loadMembership = async () => {
      if (!currentTeam || !activeProfile) {
        setCurrentMembership(null);
        return;
      }
      try {
        const membership = await getCurrentTeamMember(currentTeam.id, activeProfile.id);
        setCurrentMembership(membership);
      } catch {
        setCurrentMembership(null);
      }
    };
    loadMembership();
  }, [currentTeam, activeProfile]);

  const createTeam = async (name: string): Promise<Team> => {
    if (!activeProfile) {
      throw new Error('No active profile');
    }
    const team = await dbCreateTeam(name, activeProfile.id);
    await loadTeams();
    trackEvent('team_created', { is_first_team: teams.length === 0 });
    return team;
  };

  const createPlayer = async (name: string, dateOfBirth: string, classification?: PlayerClassification): Promise<Player> => {
    if (!activeProfile) {
      throw new Error('No active profile');
    }
    if (!currentTeam) {
      throw new Error('No team selected');
    }
    const player = await dbCreatePlayer(name, dateOfBirth, activeProfile.id, currentTeam.id, classification);
    await loadPlayers();
    return player;
  };

  const deletePlayer = async (id: string): Promise<void> => {
    await dbDeletePlayer(id);
    await loadPlayers();
  };

  const archivePlayer = async (id: string, archived = true): Promise<void> => {
    await dbArchivePlayer(id, archived);
    await loadPlayers();
  };

  const mergePlayers = async (keepPlayerId: string, mergePlayerId: string): Promise<void> => {
    if (!activeProfile) throw new Error('No active profile');
    await dbMergePlayers(keepPlayerId, mergePlayerId, activeProfile.id);
    await loadPlayers();
  };

  const renameTeam = async (newName: string): Promise<void> => {
    if (!currentTeam) throw new Error('No team selected');
    await dbUpdateTeamName(currentTeam.id, newName);
    await loadTeams();
    // Update currentTeam in place so UI reflects immediately
    setCurrentTeam({ ...currentTeam, name: newName });
  };

  const removeTeam = async (): Promise<void> => {
    if (!currentTeam) throw new Error('No team selected');
    await dbDeleteTeam(currentTeam.id);
    setCurrentTeam(null);
    setCurrentMembership(null);
    await loadTeams();
  };

  return (
    <TeamContext.Provider
      value={{
        teams,
        teamsLoading,
        currentTeam,
        players,
        currentMembership,
        setCurrentTeam: handleSetCurrentTeam,
        createTeam,
        createPlayer,
        deletePlayer,
        archivePlayer,
        mergePlayers,
        renameTeam,
        removeTeam,
        loadPlayers,
        loadTeams,
      }}
    >
      {children}
    </TeamContext.Provider>
  );
};

export const useTeam = (): TeamContextType => {
  const context = useContext(TeamContext);
  if (!context) {
    throw new Error('useTeam must be used within TeamProvider');
  }
  return context;
};
